import type { FC, ReactNode } from 'react';

import { cn } from '@/utils/cn.util';

interface SideDrawerProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Sticky action bar pinned to the bottom of the panel. */
  footer?: ReactNode;
}

/** The right-hand detail panel shared by the admin drawers: backdrop, slide-in, close button. */
const SideDrawer: FC<SideDrawerProps> = ({ isOpen, title, onClose, children, footer }) => (
  <>
    <div
      className={cn(
        'fixed inset-0 z-40 bg-black/40 transition-opacity duration-300',
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      onClick={onClose}
    />

    <div
      className={cn(
        'fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[480px] flex-col bg-white shadow-2xl',
        'overflow-hidden transition-transform duration-300 ease-in-out',
        isOpen ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      <div className="flex items-center justify-between border-b border-[var(--color-divider)] px-5 py-4">
        <h2 className="text-[15px] font-bold text-[var(--color-ink)]">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-lg p-1.5 text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)]"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
            <path d="M3.22 3.22a.75.75 0 0 1 1.06 0L8 6.94l3.72-3.72a.75.75 0 1 1 1.06 1.06L9.06 8l3.72 3.72a.75.75 0 1 1-1.06 1.06L8 9.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06L6.94 8 3.22 4.28a.75.75 0 0 1 0-1.06z" />
          </svg>
        </button>
      </div>

      {children}

      {footer ? (
        <div className="border-t border-[var(--color-divider)] bg-white px-5 py-4">{footer}</div>
      ) : null}
    </div>
  </>
);

export default SideDrawer;
